<script setup lang="ts">
import { h, ref, computed, onMounted, onUnmounted, reactive } from 'vue';
import { useI18n } from 'vue-i18n';
import { getCurrentLocale } from '@/i18n';
import { useRouter } from 'vue-router';
import { DateTime } from 'luxon';
import routerKey from '@/router/routerKey';
import { useDebounceFn } from '@vueuse/core';
import { AIR_ONLY, CRUISE, HOTEL_ONLY, LANG_ENGLISH, LANG_FRENCH, PACKAGE, MAP_ROLE_TO_DEPARTMENT, UNASSIGNED, CREATED, PRICE_MATCH, TOUR } from '@/constants';
import { useUserAuthorizationsStore } from '@/store/UserAuthorizationsStore';
import { useLayout } from '@/layout/composables/layout';
import { useMyQuoteStore } from '@/store/MyQuoteStore';
import { useViewQuoteStore } from '@/store/ViewQuoteStore';
import { useBookingQuoteStore } from '@/store/BookingQuoteStore';
import { useAdminQuoteStore } from '@/store/AdminQuoteStore';
import { useGroupTypeStore } from '@/store/GroupTypeStore';
import { UserInfo } from '@/modules/user/UserInfo';
import { useUserInfo } from '@/modules/user/useUserInfo';
import { useViewComposable } from '@/views/composable';
import { formatDate } from '@/utils/dateUtils';
import { publishMpvAnalytic } from '@/modules/analytics/analyticsService';
import { FilterMatchMode, FilterOperator } from '@primevue/core/api';
import { useDialog } from 'primevue/usedialog';
import { useToast } from 'primevue/usetoast';
import Button from 'primevue/button';
import DataTable from 'primevue/datatable';
import DatePicker from 'primevue/datepicker';
import InputText from 'primevue/inputtext';
import Select from 'primevue/select';
import TableColumn from 'primevue/column';
import LoadingSpinner from '@/components/LoadingSpinner.vue';
import BookForm from '@/components/BookForm.vue';
import ClientCopyAir from '@/components/modal/clientCopy/ClientCopyAir.vue';
import ClientCopyCruise from '@/components/modal/clientCopy/ClientCopyCruise.vue';
import ClientCopyHotel from '@/components/modal/clientCopy/ClientCopyHotel.vue';
import ClientCopyPackage from '@/components/modal/clientCopy/ClientCopyPackage.vue';
import ClientCopyTour from '@/components/modal/clientCopy/ClientCopyTour.vue';
import FileNotes from '@/components/notes/FileNotes.vue';
import FilterTable from '@/components/common/FilterTable.vue';
import Payment from '@/components/modal/Payment.vue';
import SeeMoreToursDescription from '@/components/modal/tour/SeeMoreToursDescription.vue';
import Stats from '@/components/stats/Stats.vue';
import TaskList from '@/components/notes/TaskList.vue';
import UrlQuoteModal from '@/components/modal/UrlQuoteModal.vue';
import ViewPDFModal from '@/components/modal/ViewPDFModal.vue';

let quoteId: string;
const { t } = useI18n({ useScope: 'global' });
const currentLanguage = getCurrentLocale();
const currentUser = ref<UserInfo>();
const { layoutState } = useLayout();
const { quoteSts, priorityStatus, statusPriorityNumber, renderLocalTimeFromUtc, renderDateTimeOption } = useViewComposable();
const quotes = ref([]);
const filters = ref();
const loading = ref(false);
const isLoading = ref(false);
const displayBookForm = ref(false);
const myQuoteStore = useMyQuoteStore();
const router = useRouter();
const userInfo = useUserInfo();
const viewQuoteStore = useViewQuoteStore();
const adminQuoteStore = useAdminQuoteStore();
const bookingQuoteStore = useBookingQuoteStore();
const groupTypeManagement = useGroupTypeStore();
const userAuthorizationsStore = useUserAuthorizationsStore();
const authorizations: any = userAuthorizationsStore.permissionByRole;
const isClientCopyOpen = ref(false);
const pageSize = ref([10, 25, 50, 100, 200, 400, 500]);
const maxLengthText = 70;
const currentFieldFilter = ref('');
const dataTableRef = ref();
const filterGlobalField = ref('');
const toast = useToast();

const userRolePublic = ref<any>('');

const minDateDefault = DateTime.utc().minus({ days: 60 }).toJSDate();
const maxDateDefault = new Date();

const initData = () => {
  return {
    typeOfSearch: CREATED,
    fromDate: minDateDefault,
    toDate: maxDateDefault,
    quoteType: [PACKAGE, HOTEL_ONLY, AIR_ONLY, CRUISE, TOUR],
    groupType: [],
    quoteId: ''
  };
};

const initValueFilter = reactive(initData());
const totalRecords = ref(undefined);
const groupTypeList = ref([]);

const initFilters = () => {
  filters.value = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    groupName: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    groupTypeId: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }] },
    depDateOption: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    destination: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    hotelDestinations: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    created: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }] },
    expires: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }] },
    assignedTo: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    quoteSts: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] }
  };
};
initFilters();

const quote = computed(() => viewQuoteStore.selectQuote);

const getRole = async () => {
  const currentUser = await userInfo.getUserInfo();
  const userRole = currentUser?.role?.toUpperCase();
  if (MAP_ROLE_TO_DEPARTMENT[userRole]) {
    userRolePublic.value = MAP_ROLE_TO_DEPARTMENT[userRole];
  } else {
    userRolePublic.value = userRole;
  }
};

getRole();

const displayDescription = (quoteType: string) => {
  return quoteType !== CRUISE && quoteType !== AIR_ONLY;
};

const getgroupTypeData = async () => {
  await groupTypeManagement.getGroupTypeList().then((response) => {
    groupTypeList.value = response.data;
    initValueFilter.groupType = getGroupTypeIDs(response.data);
  });
};

const getGroupTypeIDs = (groupTypes) => {
  return groupTypes.map((item) => item?.sk);
};

const handleFilterData = (dataFilter) => {
  if (dataFilter) {
    initValueFilter.typeOfSearch = dataFilter.typeOfSearch;
    initValueFilter.fromDate = dataFilter.fromDate || initData().fromDate;
    initValueFilter.toDate = dataFilter.toDate || initData().toDate;
    initValueFilter.quoteType = dataFilter.quoteType;
    initValueFilter.groupType = dataFilter.groupType;
    initValueFilter.quoteId = dataFilter.quoteId;
  } else {
    initValueFilter.typeOfSearch = initData().typeOfSearch;
    initValueFilter.fromDate = initData().fromDate;
    initValueFilter.toDate = initData().toDate;
    initValueFilter.quoteType = initData().quoteType;
    initValueFilter.groupType = getGroupTypeIDs(groupTypeList.value);
    initValueFilter.quoteId = initData().quoteId;
  }
  getListQuote();
};

const hasUnreadNotes = (notes: any) => {
  return notes.some((note: any) => note.createdBy === 'EXTERNAL' && !note.externalRead);
};

const getListQuote = () => {
  const fromDate = new Date(initValueFilter.fromDate).setHours(0, 0, 0, 0);
  const toDate = new Date(initValueFilter.toDate).setHours(23, 59, 59, 0);
  const payload = {
    ...initValueFilter,
    fromDate: new Date(fromDate).toISOString(),
    toDate: new Date(toDate).toISOString()
  };
  isLoading.value = true;
  myQuoteStore
    .getListQuotes(currentUser.value?.email, MAP_ROLE_TO_DEPARTMENT[currentUser.value?.role], payload)
    .then((res) => {
      res.forEach((data) => {
        if (data.quoteID?.includes('IQ')) {
          data.isInstaQuote = true;
        } else {
          data.isInstaQuote = false;
        }
        data.created = renderLocalTimeFromUtc(data.created);
        data.expires = renderLocalTimeFromUtc(data.expires);
        data.depDateOption = renderDateTimeOption(data.depDateOption);
        data.assignedTo = data.assignedTo === UNASSIGNED ? renderInstaQuoteAssignee(data.quoteID) : data.assignedTo;
        data.linkedQuotes = convertString(data.linkedQuotes);
      });
      quotes.value = res;
      quotes.value.forEach((quote: any) => {
        quote.hasNoteToRead = hasUnreadNotes(quote.notes);
      });
      totalRecords.value = res.length;
      loading.value = false;
    })
    .finally(() => (isLoading.value = false));
};

const mapStatus = (status) => {
  const statusRs = quoteSts.value.find((item) => item.value === status);
  return statusRs ? statusRs.label : '';
};

const renderInstaQuoteAssignee = (quoteID) => {
  return quoteID?.includes('IQ') ? t('common.instaquote') : t('status.unassigned');
};

const handleFilterApply = (filterCallback, filterField) => {
  currentFieldFilter.value = filterField;
  filterCallback();
};

const handleClearFilter = (filterCallback, filterField) => {
  currentFieldFilter.value = filterField;
  filterCallback();
};

const clearFilter = () => {
  initFilters();
  dataTableRef.value.d_sortField = 0;
  filterGlobalField.value = '';
  getListQuote();
};

const dialog = useDialog();
const openBookForm = async (qId, status, priorityStatus) => {
  viewQuoteStore.getAction('book');

  isLoading.value = true;
  await bookingQuoteStore.getQuoteDetail(qId).finally(() => {
    isLoading.value = false;
  });

  quoteId = qId;
  displayBookForm.value = true;
  const dialogRef = dialog.open(BookForm, {
    data: {
      qId: qId
    },
    props: {
      modal: true,
      maximizable: true,
      status: status,
      isManagement: false,
      priorityStatus: priorityStatus,
      style: {
        width: '90vw'
      }
    },
    emits: {
      isSave: async (value) => {
        if (value === true) {
          dialogRef.close();
          await adminQuoteStore.updateQuoteStatus(quoteId, 'BOOKING');
          getListQuote();
        } else {
          dialogRef.close();
        }
      }
    },
    templates: {
      header: () => {
        return [h('div', { class: 'flex align-items-center' }, [t('booking.hint_header'), h('i', { class: 'ml-3 text-4xl fa-solid fa-hand-point-right' })])];
      }
    },
    onClose: () => {
      document.getElementsByTagName('body')[0].classList.remove('p-overflow-hidden-2');
    }
  });
};

const openExtNotes = async (qId: string, isRead: boolean, data: any) => {
  await myQuoteStore.getNote(qId);
  quoteId = qId;
  dialog.open(FileNotes, {
    data: {
      page: 'MYQUOTE',
      quoteId,
      service: myQuoteStore,
      isRead: isRead,
      isMyQuote: true,
      data: data
    },
    props: {
      header: t('note.file_notes'),
      modal: true,
      maximizable: true,
      style: {
        width: '90vw'
      }
    },
    onClose: () => {
      getListQuote();
    }
  });
};

const handleView = async (quote) => {
  isLoading.value = true;
  viewQuoteStore.getAction('view');
  viewQuoteStore
    .viewPDF(quote?.quoteID)
    .then(() => {
      dialog.open(ViewPDFModal, {
        props: {
          header: t('pdf.view_pdf'),
          style: { width: '90%' },
          modal: true
        }
      });
    })
    .catch(() => {
      toast.add({ severity: 'info', summary: '', detail: t('my_quote.toastMessage.pdfNotGenerated'), life: 3000 });
    })
    .finally(() => {
      isLoading.value = false;
    });
};

const handleRequote = async (id) => {
  await viewQuoteStore.reQuote(id);
  viewQuoteStore.getActionRequoteFromWorksheet(true);
  if (quote.value.qType === AIR_ONLY) {
    viewQuoteStore.getAction('requote');
    router.push({ name: routerKey.AIR });
  } else if (quote.value.qType === HOTEL_ONLY) {
    viewQuoteStore.getAction('requote');
    router.push({ name: routerKey.HOTEL });
  } else if (quote.value.qType === CRUISE) {
    viewQuoteStore.getAction('requote');
    router.push({ name: routerKey.CRUISE });
  } else if (quote.value.qType === PACKAGE) {
    viewQuoteStore.getAction('requote');
    router.push({ name: routerKey.HOME });
  } else {
    viewQuoteStore.getAction('requote');
    router.push({ name: routerKey.MULTI_TOURS });
  }
};

const handleClientCopy = async (id) => {
  viewQuoteStore.getAction('clientCopy');
  isLoading.value = true;
  await viewQuoteStore.handleViewClientCopy(id).finally(() => {
    isLoading.value = false;
  });
  if (quote.value.pdf) {
    isClientCopyOpen.value = true;
  } else {
    toast.add({ severity: 'info', summary: '', detail: t('my_quote.toastMessage.clientCopyNotCreated'), life: 3000 });
  }
};

const handleEdoc = (id) => {
  const payload = {
    id: id,
    type: 'E_DOC'
  };
  viewQuoteStore.handleViewUrlQuote(payload);
  dialog.open(UrlQuoteModal, {
    props: {
      header: t('view.edoc_view'),
      style: {
        width: '95vw'
      },
      breakpoints: {
        '960px': '75vw',
        '640px': '90vw'
      },
      modal: true
    }
  });
};

const handlePayment = (quote: any) => {
  const quoteIdArray = quote?.bookNumber?.split(',');
  const dialogRef = dialog.open(Payment, {
    props: {
      header: t('my_quote.payment_for') + quoteIdArray[0],
      modal: true
    },
    data: {
      quote
    },
    emits: {
      isSave: (value) => {
        if (value === true) {
          dialogRef.close();
          getListQuote();
        }
      }
    }
  });
};

const handleInvoice = (id) => {
  const payload = {
    id: id,
    type: 'INVOICE'
  };
  viewQuoteStore.handleViewUrlQuote(payload);
  dialog.open(UrlQuoteModal, {
    props: {
      header: t('view.invoice_view'),
      style: {
        width: '95vw'
      },
      breakpoints: {
        '960px': '75vw',
        '640px': '90vw'
      },
      modal: true
    }
  });
};

const statusEvent = (status) => {
  filters.value.quoteSts.constraints[0].value = status;
  document.getElementById('dtMyquote').scrollIntoView({
    behavior: 'smooth',
    block: 'start',
    inline: 'nearest'
  });
};

const checkScreenWidth = useDebounceFn(() => {
  if (window.innerWidth < 1400) {
    layoutState.staticMenuDesktopInactive.value = true;
  } else {
    layoutState.staticMenuDesktopInactive.value = false;
  }
}, 500);

const getGroupTypeName = (groupTypeId) => {
  const groupType = groupTypeList.value.find((item) => item.sk === groupTypeId);
  if (groupType) {
    return currentLanguage === LANG_ENGLISH ? groupType.name : groupType.nameFr;
  }
  return '';
};

const formatDescription = (descriptionList: any) => {
  return descriptionList.join('\n');
};

const formatDestination = (destinationList: any) => {
  return destinationList?.replace(/,/g, '\n');
};

const openSeeMore = (dataField: string, header: string, isBookingId?: boolean) => {
  const dialogRef = dialog.open(SeeMoreToursDescription, {
    props: {
      header: `${header}`,
      modal: true,
      style: {
        width: isBookingId ? '20vw' : '50vw'
      }
    },
    data: {
      dataField: dataField
    },
    emits: {
      onClose: () => {
        dialogRef.close();
      }
    }
  });
};

const checkLengthSeemore = (destination?: string) => {
  if (formatDestination(destination)?.length) {
    return formatDestination(destination)?.match(/\n/g)?.length > 2 || formatDestination(destination).length > maxLengthText;
  }

  return false;
};

const convertString = (quoteIds: any) => {
  return quoteIds?.join(', ');
};

// Temporary fix due to issue in PrimeVue 4.2.5 for frozen columns
// https://github.com/primefaces/primevue/issues/6067
// To be removed as soon as we have a fix
const frozenTableMyQuote = () => {
  const table = dataTableRef.value?.$el.querySelector('.p-datatable-thead');
  if (table) {
    const colWidths = table.querySelectorAll('th');
    document.documentElement.style.setProperty('--col-width-1', 0 + 'px');
    document.documentElement.style.setProperty('--col-width-2', colWidths[0].offsetWidth + 'px');
    document.documentElement.style.setProperty('--col-width-3', colWidths[0].offsetWidth + colWidths[1].offsetWidth + 'px');
  }
};

onMounted(async () => {
  currentUser.value = await userInfo.getUserInfo();
  await getgroupTypeData();
  await Promise.all([myQuoteStore.getReminders({ email: currentUser.value?.email }), getListQuote()]);
  publishMpvAnalytic('My Quotes Page');

  layoutState.staticMenuDesktopInactive.value = true;
  window.addEventListener('resize', checkScreenWidth);
  frozenTableMyQuote();
});

onUnmounted(() => {
  window.removeEventListener('resize', checkScreenWidth);
});
</script>

<template>
  <LoadingSpinner v-if="isLoading" />
  <Stats @status-event="statusEvent($event)" />
  <div class="grid" v-if="authorizations?.my_quote?.reminder_view">
    <div class="col-12">
      <div class="card">
        <h2>{{ $t('task_list.reminders') }}</h2>
        <TaskList :email="currentUser?.email" />
      </div>
    </div>
  </div>
  <div class="grid my-quote">
    <div class="col-12">
      <div class="card">
        <div class="mb-5 flex justify-content-between flex-col sm:flex-row">
          <div class="mb-5">
            <h2>{{ $t('my_quote.my_quote') }}</h2>
            <p>
              {{ $t('my_quote.title_one') }}
              <br />
              {{ $t('my_quote.title_two') }}
              <br />
              {{ $t('my_quote.title_three') }}
              <br /><br />
              --
              <span v-if="currentLanguage === 'fr'"
                >{{ $t('my_quote.your') }} {{ $t('my_quote.team') }} {{ $t('common.from').toLowerCase() }}
                <span class="font-bold">{{ $t('my_quote.group_travel').toLowerCase() }}</span>
                {{ $t('common.from').toLowerCase() }}
                <span class="text-primary font-bold">{{ $t('my_quote.acv') + ' ' }} </span>
              </span>
              <span v-else
                >{{ $t('my_quote.your') }}
                <span class="text-primary font-bold">{{ $t('my_quote.acv') + ' ' }} </span>
                <span class="font-bold">{{ $t('my_quote.group_travel') }}</span>
                {{ $t('my_quote.team') }}</span
              >
            </p>
          </div>

          <div class="white-space-nowrap">
            <h5>{{ $t('quote_management.quote_id_legend') }}</h5>
            <div>
              <b>IQ</b> - {{ $t('status.instant_package_quote') }}<br />
              <b>IM</b> - {{ $t('status.manual_package_quote') }}<br />
              <b>IT</b> - {{ $t('status.instant_mdt_quote') }}<br />
              <b>TM</b> - {{ $t('status.manual_mdt_quote') }}<br />
              <b>AM</b> - {{ $t('status.manual_air_quote') }}<br />
              <b>HM</b> - {{ $t('status.manual_hotel_quote') }}<br />
              <b>CM</b> - {{ $t('status.manual_cruise_quote') }}
            </div>
          </div>
        </div>
        <div class="filter-bar">
          <FilterTable :isShowQuoteId="true" :dataFilter="initValueFilter" :groupTypeList="groupTypeList" @handleFilterData="handleFilterData"></FilterTable>
        </div>
        <DataTable
          :value="quotes"
          ref="dataTableRef"
          :paginator="true"
          class="p-datatable-gridlines w-full p-datatable-sm"
          :rows="pageSize[0]"
          dataKey="id"
          :rowHover="true"
          v-model:filters="filters"
          :totalRecords="totalRecords"
          scrollable
          scrollHeight="900px"
          filterDisplay="menu"
          :loading="loading"
          responsiveLayout="stack"
          :rowsPerPageOptions="pageSize"
          :globalFilterFields="['quoteID', 'groupName', 'groupType', 'depDateOptions', 'destination', 'hotelDestinations', 'assignedTo', 'quoteSts', 'linkedQuotes']"
          id="dtMyquote"
        >
          <template #header>
            <div class="flex justify-content-between flex-col sm:flex-row">
              <div class="flex gap-2 align-items-center">
                <Button type="button" icon="pi pi-filter-slash" :label="$t('common.buttons.clear')" class="p-button-secondary mb-2" @click="clearFilter()" />
                <div>({{ $t('quote_management.hint_message') }} )</div>
              </div>
              <span class="p-input-icon-left mb-2">
                <i class="pi pi-search" />
                <InputText v-model="filters['global'].value" :placeholder="$t('my_quote.search_keyword')" class="w-full" />
              </span>
            </div>
          </template>
          <template #empty> {{ $t('my_quote.no_quotes_found') }} </template>
          <template #loading> {{ $t('common.generic.loading') }}. {{ $t('my_quote.please_wait') }}. </template>
          <TableColumn field="quoteID" :header="$t('my_quote.quote_id')" class="min-w-7rem" :class="currentLanguage === LANG_FRENCH ? 'max-width-fr' : ''" sortable frozen>
            <template #body="{ data }">
              <div>{{ data.quoteID }}</div>
              <div class="linked-quotes mt-3 mb-2">
                <strong class="text-sm">{{ $t('worksheet.linked_to') }}:</strong>
                <div class="mb-1 text-elipsis font-normal">{{ formatDestination(data.linkedQuotes) || '-' }}</div>
                <span v-if="checkLengthSeemore(data.linkedQuotes)" class="view-more">
                  <a class="text-blue-600 font-bold text-sm underline cursor-pointer text-left uppercase" @click="openSeeMore(formatDestination(data.linkedQuotes), $t('worksheet.linked_quotes'), true)">{{ $t('common.labels.see_more') }}</a>
                </span>
              </div>
            </template>
          </TableColumn>
          <TableColumn :show-filter-operator="false" :show-add-button="false" field="groupName" :header="$t('my_quote.group_name')" class="break-word min-w-12rem" filter frozen>
            <template #body="{ data }">
              <p class="long-text-tooltip" v-tooltip="data.groupName">
                {{ data.groupName }}
              </p>
            </template>
            <template #filter="{ filterModel }">
              <InputText type="text" v-model="filterModel.value" class="p-column-filter" :placeholder="$t('voucher.tableHeader.groupNamePlaceHolder')" />
            </template>
            <template #filterclear="{ filterCallback }">
              <Button type="button" icon="pi pi-times" @click="handleClearFilter(filterCallback, 'groupName')" class="p-button-secondary"></Button>
            </template>
            <template #filterapply="{ filterCallback }">
              <Button type="button" icon="pi pi-check" @click="handleFilterApply(filterCallback, 'groupName')" class="p-button-success"></Button>
            </template>
          </TableColumn>
          <TableColumn :show-filter-operator="false" field="groupTypeId" :show-add-button="false" :header="$t('my_quote.group_type')" class="break-word min-w-10rem" frozen>
            <template #body="{ data }">
              <span :class="'customer-badge'">{{ getGroupTypeName(data.groupTypeId) }}</span>
            </template>
            <template #filter="{ filterModel }">
              <Select v-model="filterModel.value" :options="groupTypeList" :optionLabel="currentLanguage === LANG_ENGLISH ? 'name' : 'nameFr'" optionValue="sk" :placeholder="$t('common.select_one')" class="p-column-filter w-17rem" showClear />
            </template>
            <template #filterclear="{ filterCallback }">
              <Button type="button" icon="pi pi-times" @click="handleClearFilter(filterCallback, 'groupTypeId')" class="p-button-secondary"></Button>
            </template>
            <template #filterapply="{ filterCallback }">
              <Button type="button" icon="pi pi-check" @click="handleFilterApply(filterCallback, 'groupTypeId')" class="p-button-success"></Button>
            </template>
          </TableColumn>
          <TableColumn :show-filter-operator="false" :show-add-button="false" :header="$t('my_quote.dep_date')" filterField="depDateOption" filter class="min-w-11rem">
            <template #body="{ data }">
              <span class="break-space">
                {{ data.depDateOption }}
              </span>
            </template>
            <template #filter="{ filterModel }">
              <InputText type="text" v-model="filterModel.value" class="p-column-filter" :placeholder="$t('voucher.tableHeader.searchByDeptDate')" />
            </template>
            <template #filterclear="{ filterCallback }">
              <Button type="button" icon="pi pi-times" @click="handleClearFilter(filterCallback, 'depDateOption')" class="p-button-secondary"></Button>
            </template>
            <template #filterapply="{ filterCallback }">
              <Button type="button" icon="pi pi-check" @click="handleFilterApply(filterCallback, 'depDateOption')" class="p-button-success"></Button>
            </template>
          </TableColumn>
          <TableColumn :show-filter-operator="false" :show-add-button="false" :header="$t('my_quote.destination')" filterField="destination" style="min-width: 300px">
            <template #filter="{ filterModel }">
              <InputText type="text" v-model="filterModel.value" class="p-column-filter" :placeholder="$t('voucher.tableHeader.searchByCountry')" />
            </template>
            <template #body="{ data }">
              <div>
                <p class="text-elipsis">
                  {{ formatDestination(data.destination) }}
                </p>
                <span v-if="checkLengthSeemore(data.destination)" class="view-more">
                  <a class="text-blue-600 font-bold underline cursor-pointer text-left uppercase" @click="openSeeMore(formatDestination(data.destination), $t('my_quote.destination'))">{{ $t('common.labels.see_more') }}</a>
                </span>
              </div>
            </template>
            <template #filterclear="{ filterCallback }">
              <Button type="button" icon="pi pi-times" @click="handleClearFilter(filterCallback, 'destination')" class="p-button-secondary"></Button>
            </template>
            <template #filterapply="{ filterCallback }">
              <Button type="button" icon="pi pi-check" @click="handleFilterApply(filterCallback, 'destination')" class="p-button-success"></Button>
            </template>
          </TableColumn>
          <TableColumn :show-filter-operator="false" :show-add-button="false" :header="$t('hotel_contract.description')" filterField="hotelDestinations" style="min-width: 300px">
            <template #filter="{ filterModel }">
              <InputText type="text" v-model="filterModel.value" class="p-column-filter" :placeholder="$t('voucher.tableHeader.search_by_description')" />
            </template>
            <template #body="{ data }">
              <div v-if="displayDescription(data.quoteType)">
                <p class="text-elipsis">
                  {{ formatDescription(data.hotelDestinations) }}
                </p>
                <span v-if="data.hotelDestinations && formatDescription(data.hotelDestinations).length > maxLengthText" class="view-more">
                  <a class="text-blue-600 font-bold underline cursor-pointer text-left uppercase" @click="openSeeMore(formatDescription(data.hotelDestinations), $t('hotel_contract.description'))">{{ $t('common.labels.see_more') }}</a>
                </span>
              </div>
            </template>
            <template #filterclear="{ filterCallback }">
              <Button type="button" icon="pi pi-times" @click="handleClearFilter(filterCallback, 'hotelDestinations')" class="p-button-secondary"></Button>
            </template>
            <template #filterapply="{ filterCallback }">
              <Button type="button" icon="pi pi-check" @click="handleFilterApply(filterCallback, 'hotelDestinations')" class="p-button-success"></Button>
            </template>
          </TableColumn>
          <TableColumn :show-filter-operator="false" :show-add-button="false" :header="$t('my_quote.created')" filterField="created" dataType="date" class="min-w-10rem">
            <template #body="{ data }">
              <p class="mb-0">{{ formatDate(data.created, 'dd-LLL-yyyy', undefined, currentLanguage) }}</p>
              <p>{{ formatDate(data.created, 'HH:mm:ss') }}</p>
            </template>
            <template #filter="{ filterModel }">
              <DatePicker v-model="filterModel.value" dateFormat="dd-M-yy" :placeholder="$t('common.date_format')">
                <template #footer>
                  <p class="text-center">
                    <small>{{ $t('common.press_space') }}</small>
                  </p>
                </template>
              </DatePicker>
            </template>
            <template #filterclear="{ filterCallback }">
              <Button type="button" icon="pi pi-times" @click="handleClearFilter(filterCallback, 'created')" class="p-button-secondary"></Button>
            </template>
            <template #filterapply="{ filterCallback }">
              <Button type="button" icon="pi pi-check" @click="handleFilterApply(filterCallback, 'created')" class="p-button-success"></Button>
            </template>
          </TableColumn>
          <TableColumn :show-filter-operator="false" :show-add-button="false" :header="$t('my_quote.expires')" filterField="expires" dataType="date" class="min-w-11rem">
            <template #body="{ data }">
              {{ formatDate(data.expires, 'dd-LLL-yyyy', false, currentLanguage) }}
            </template>
            <template #filter="{ filterModel }">
              <DatePicker v-model="filterModel.value" dateFormat="dd-M-yy" :placeholder="$t('common.date_format')">
                <template #footer>
                  <p class="text-center">
                    <small>{{ $t('common.press_space') }}</small>
                  </p>
                </template>
              </DatePicker>
            </template>
            <template #filterclear="{ filterCallback }">
              <Button type="button" icon="pi pi-times" @click="handleClearFilter(filterCallback, 'expires')" class="p-button-secondary"></Button>
            </template>
            <template #filterapply="{ filterCallback }">
              <Button type="button" icon="pi pi-check" @click="handleFilterApply(filterCallback, 'expires')" class="p-button-success"></Button>
            </template>
          </TableColumn>
          <TableColumn :show-filter-operator="false" :show-add-button="false" :header="$t('my_quote.coordinator')" filterField="assignedTo" class="min-w-10rem">
            <template #body="{ data }">
              <p class="m-0">{{ data.assignedTo }}</p>
              <p class="mt-1">{{ data.coEmail }}</p>
            </template>
            <template #filter="{ filterModel }">
              <InputText type="text" v-model="filterModel.value" class="p-column-filter" :placeholder="$t('voucher.tableHeader.coorPlaceHolder')" />
            </template>
            <template #filterclear="{ filterCallback }">
              <Button type="button" icon="pi pi-times" @click="handleClearFilter(filterCallback, 'assignedTo')" class="p-button-secondary"></Button>
            </template>
            <template #filterapply="{ filterCallback }">
              <Button type="button" icon="pi pi-check" @click="handleFilterApply(filterCallback, 'assignedTo')" class="p-button-success"></Button>
            </template>
          </TableColumn>
          <TableColumn :show-filter-operator="false" :show-add-button="false" field="quoteSts" :header="$t('my_quote.status')" filterField="quoteSts" class="min-w-10rem">
            <template #body="{ data }">
              <div class="flex flex-col gap-3">
                <span class="font-bold uppercase">{{ mapStatus(data.quoteSts) }}</span>
                <div class="font-bold" v-if="data.bookingId">
                  <p class="text-elipsis">
                    {{ formatDestination(data.bookingId) }}
                  </p>
                  <span v-if="checkLengthSeemore(data.bookingId)" class="view-more">
                    <a class="text-blue-600 font-bold underline cursor-pointer text-left uppercase" @click="openSeeMore(formatDestination(data.bookingId), $t('my_quote.booking_number'), true)">{{ $t('common.labels.see_more') }}</a>
                  </span>
                </div>
              </div>
            </template>
            <template #filter="{ filterModel }">
              <Select v-model="filterModel.value" :options="quoteSts" optionLabel="label" optionValue="value" :placeholder="$t('common.any')" class="p-column-filter w-14rem" :showClear="true">
                <template #option="slotProps">
                  <option>{{ slotProps.option.label }}</option>
                </template>
              </Select>
            </template>
            <template #filterclear="{ filterCallback }">
              <Button type="button" icon="pi pi-times" @click="handleClearFilter(filterCallback, 'status')" class="p-button-secondary"></Button>
            </template>
            <template #filterapply="{ filterCallback }">
              <Button type="button" icon="pi pi-check" @click="handleFilterApply(filterCallback, 'status')" class="p-button-success"></Button>
            </template>
          </TableColumn>
          <TableColumn :header="$t('my_quote.actions')" frozen alignFrozen="right" class="text-center action-column mx-auto">
            <template #body="{ data }">
              <div class="flex flex-wrap w-full">
                <div class="flex basis p-2" v-if="authorizations?.my_quote?.pdf_view">
                  <Button @click="handleView(data)" :label="$t('my_quote.view')" :disabled="priorityStatus.get(data.quoteSts) <= statusPriorityNumber.TWO" class="p-button-outlined p-button-xs border-noround w-full" />
                </div>
                <div class="flex basis p-2" v-if="priorityStatus.get(data.quoteSts) > statusPriorityNumber.TWO && priorityStatus.get(data.quoteSts) <= statusPriorityNumber.FOUR && authorizations?.my_quote?.book_view">
                  <Button
                    :id="data.quoteID"
                    :label="$t('my_quote.book')"
                    class="p-button-outlined p-button-xs border-noround w-full"
                    @click="openBookForm(data.quoteID, data.quoteSts, priorityStatus.get(data.quoteSts))"
                    :disabled="priorityStatus.get(data.quoteSts) == statusPriorityNumber.FOUR"
                  />
                </div>
                <div class="flex basis p-2" v-if="priorityStatus.get(data.quoteSts) <= statusPriorityNumber.FOUR && authorizations?.my_quote?.re_quote_edit">
                  <Button
                    @click="handleRequote(data.quoteID)"
                    :label="$t('my_quote.re_quote')"
                    class="p-button-outlined p-button-xs border-noround w-full"
                    :disabled="priorityStatus.get(data.quoteSts) == statusPriorityNumber.FOUR || priorityStatus.get(data.quoteSts) <= statusPriorityNumber.TWO"
                  />
                </div>
                <div class="flex basis p-2" v-if="priorityStatus.get(data.quoteSts) <= statusPriorityNumber.FOUR && authorizations?.my_quote?.client_copy_view">
                  <Button
                    @click="handleClientCopy(data.quoteID)"
                    :label="$t('my_quote.client_copy')"
                    :disabled="priorityStatus.get(data.quoteSts) <= statusPriorityNumber.TWO || data.quoteSts === PRICE_MATCH"
                    class="p-button-outlined p-button-xs border-noround w-full"
                  />
                </div>
                <div class="flex basis p-2" v-if="priorityStatus.get(data.quoteSts) >= statusPriorityNumber.FIVE && authorizations?.my_quote?.payment_add">
                  <Button :label="$t('my_quote.payment')" class="p-button-outlined p-button-xs border-noround w-full" @click="handlePayment(data)" />
                </div>
                <div class="flex basis p-2" v-if="priorityStatus.get(data.quoteSts) > statusPriorityNumber.FOUR && authorizations?.my_quote?.edocs_view">
                  <Button :label="$t('my_quote.e_doc')" :disabled="!data.bookNumber" class="p-button-outlined p-button-xs border-noround w-full" @click="handleEdoc(data.bookingId)" />
                </div>
                <div class="flex basis p-2" v-if="priorityStatus.get(data.quoteSts) > statusPriorityNumber.FOUR && authorizations?.my_quote?.invoice">
                  <Button :label="$t('my_quote.invoice')" :disabled="!data.bookNumber" class="p-button-outlined p-button-xs border-noround w-full" @click="handleInvoice(data.bookingId)" />
                </div>
                <div class="flex basis p-2" v-if="priorityStatus.get(data.quoteSts) >= statusPriorityNumber.FIVE">
                  <Button :id="data.quoteID" :label="$t('my_quote.roomingList')" class="p-button-outlined p-button-xs border-noround w-full" @click="openBookForm(data.quoteID, data.quoteSts, priorityStatus.get(data.quoteSts))" />
                </div>
                <div v-if="authorizations?.my_quote?.note_view" class="flex basis p-2">
                  <Button :id="data.quoteID" class="p-button-outlined p-button-xs border-noround w-full" @click="openExtNotes(data.quoteID, data.hasNoteToRead, data)">
                    <span class="p-button-label">{{ $t('my_quote.notes') }}</span>
                    <span v-if="data.hasNoteToRead">
                      <i class="pi pi-exclamation-circle text-red-500"></i>
                    </span>
                  </Button>
                </div>
              </div>
            </template>
          </TableColumn>
        </DataTable>
        <div>
          <ClientCopyAir v-if="quote" :quote="quote" :show="isClientCopyOpen && quote.qType === AIR_ONLY" @close="isClientCopyOpen = false" :key="quote" />
          <ClientCopyHotel v-if="quote" :quote="quote" :show="isClientCopyOpen && quote.qType === HOTEL_ONLY" @close="isClientCopyOpen = false" :key="quote" />
          <ClientCopyPackage v-if="quote" :quote="quote" :show="isClientCopyOpen && quote.qType === PACKAGE" @close="isClientCopyOpen = false" :key="quote" />
          <ClientCopyCruise v-if="quote" :quote="quote" :show="isClientCopyOpen && quote.qType === CRUISE" @close="isClientCopyOpen = false" :key="quote" />
          <ClientCopyTour v-if="quote" :quote="quote" :show="isClientCopyOpen && quote.qType === TOUR" @close="isClientCopyOpen = false" :key="quote" />
        </div>
      </div>
    </div>
  </div>
</template>
